import { useEffect, useMemo, useState } from 'react';
import {
  Box, Stack, Typography, Button, IconButton, TextField, Tooltip,
  FormControl, Select, MenuItem, ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EventIcon from '@mui/icons-material/Event';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import { useTranslation } from 'react-i18next';

dayjs.extend(isoWeek);
import { PmCard, PmPill } from '../../../components/pm';
import { applyDayDelta, pxToDayDelta, type DragMode } from '../lib/ganttDrag';
import { apLabel } from '../lib/apLabel';
import { splitIsoDateTime } from '../lib/dateTimeIso';
import { MEETING_TYPES } from './MeetingEditDialog';
import { meetingTypeSymbol } from '../lib/meetingTypeSymbol';

export interface GanttTask {
  id: string;
  name: string;
  abschluss_start: string | null;
  abschluss_end: string | null;
  state?: string | null;
}

export interface ApMeeting {
  id: string;
  titel: string | null;
  typ: string;
  datum: string | null;
}

export interface NewTaskFields {
  name: string;
  abschluss_start: string | null;
  abschluss_end: string | null;
}

export interface NewMeetingFields {
  titel: string;
  date: string;
  time: string;
  typ: string;
}

interface ApTaskGanttProps {
  ap: { id: string; nr: string; titel: string | null; start_date: string; end_date: string };
  tasks: GanttTask[];
  meetings: ApMeeting[];
  onCreateTask: (fields: NewTaskFields) => Promise<void>;
  onUpdateSchedule: (taskId: string, start: string, end: string) => Promise<void>;
  onEditTask: (task: GanttTask) => void;
  onCreateMeeting: (fields: NewMeetingFields) => Promise<void>;
  onEditMeeting: (meeting: ApMeeting) => void;
}

const TASK_STATE_COLOR: Record<string, string> = {
  PLANNED: 'var(--pm-gray-400)',
  STARTED: 'var(--pm-info)',
  IN_PROGRESS: 'var(--pm-info)',
  COMPLETED: 'var(--pm-success)',
  CANCELED: 'var(--pm-error)',
};

const NAME_COL = 200;

interface DragState {
  taskId: string;
  mode: DragMode;
  startX: number;
  trackW: number;
  origStart: string;
  origEnd: string;
}

type AddMode = 'task' | 'meeting' | null;

/**
 * Mini-Gantt fuer EIN Arbeitspaket: Zeitachse = AP-Zeitraum, Zeilen = die dem AP
 * zugeordneten pm-api-Tasks (Balken aus abschluss_start/abschluss_end), darueber
 * eine Meeting-Spur (Rauten am Datum).
 * - „Aufgabe" / „Meeting" oeffnen je eine schlanke Inline-Anlage.
 * - Task-Balken laesst sich ziehen (Mitte verschiebt, Kanten = Start/Ende).
 * - Stift (Task) bzw. Klick auf die Raute (Meeting) oeffnet den Bearbeiten-Dialog.
 */
export function ApTaskGantt({
  ap, tasks, meetings, onCreateTask, onUpdateSchedule, onEditTask, onCreateMeeting, onEditMeeting,
}: ApTaskGanttProps) {
  const { t } = useTranslation('projects');
  const { t: tc } = useTranslation('common');

  const [addMode, setAddMode] = useState<AddMode>(null);
  const [busy, setBusy] = useState(false);
  const [zoom, setZoom] = useState<'week' | 'month'>('week');
  const [tForm, setTForm] = useState<NewTaskFields>({ name: '', abschluss_start: ap.start_date, abschluss_end: ap.end_date });
  const [mForm, setMForm] = useState<NewMeetingFields>({ titel: '', date: ap.start_date, time: '', typ: 'Team Meeting' });

  const apStart = useMemo(() => dayjs(ap.start_date), [ap.start_date]);
  const apEnd = useMemo(() => dayjs(ap.end_date), [ap.end_date]);
  const totalDays = Math.max(1, apEnd.diff(apStart, 'day'));

  // Position eines Datums in % der AP-Zeitachse (auf 0..100 geclamped).
  const pct = (date: string | null): number | null => {
    if (!date) return null;
    const d = dayjs(date).diff(apStart, 'day');
    return Math.max(0, Math.min(100, (d / totalDays) * 100));
  };

  // Zeitachsen-Raster: Marken je Kalenderwoche bzw. Monat ueber den AP-Zeitraum.
  const headerMarks = useMemo(() => {
    const out: Array<{ left: number; label: string }> = [];
    let cursor = zoom === 'week' ? apStart.startOf('isoWeek') : apStart.startOf('month');
    // Sicherung gegen Endlosschleifen bei degenerierten Zeitraeumen.
    for (let i = 0; i < 240 && (cursor.isBefore(apEnd) || cursor.isSame(apEnd, 'day')); i++) {
      const offset = cursor.diff(apStart, 'day');
      const leftPct = (offset / totalDays) * 100;
      if (leftPct >= -1.5 && leftPct <= 100) {
        out.push({
          left: Math.max(0, Math.min(100, leftPct)),
          label: zoom === 'week' ? `KW ${cursor.isoWeek()}` : cursor.format('MMM YY'),
        });
      }
      cursor = zoom === 'week' ? cursor.add(7, 'day') : cursor.add(1, 'month').startOf('month');
    }
    return out;
  }, [apStart, apEnd, totalDays, zoom]);

  // --- Drag (Verschieben / Kanten anpassen) ---
  const [drag, setDrag] = useState<DragState | null>(null);
  const [preview, setPreview] = useState<{ start: string; end: string } | null>(null);

  useEffect(() => {
    if (!drag) return;
    const pxPerDay = drag.trackW / totalDays;
    const onMove = (e: MouseEvent) => {
      const dd = pxToDayDelta(e.clientX - drag.startX, pxPerDay);
      setPreview(applyDayDelta(drag.origStart, drag.origEnd, drag.mode, dd));
    };
    const onUp = (e: MouseEvent) => {
      const dd = pxToDayDelta(e.clientX - drag.startX, pxPerDay);
      const res = applyDayDelta(drag.origStart, drag.origEnd, drag.mode, dd);
      const d = drag;
      setDrag(null);
      setPreview(null);
      if (res.start !== d.origStart || res.end !== d.origEnd) {
        void onUpdateSchedule(d.taskId, res.start, res.end);
      }
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
  }, [drag, totalDays, onUpdateSchedule]);

  const startDrag = (e: React.MouseEvent, task: GanttTask, mode: DragMode) => {
    if (!task.abschluss_start || !task.abschluss_end) return;
    e.preventDefault();
    e.stopPropagation();
    const track = (e.currentTarget as HTMLElement).closest('[data-task-track]') as HTMLElement | null;
    const trackW = track ? track.getBoundingClientRect().width : 0;
    setDrag({ taskId: task.id, mode, startX: e.clientX, trackW, origStart: task.abschluss_start, origEnd: task.abschluss_end });
    setPreview({ start: task.abschluss_start, end: task.abschluss_end });
  };

  // --- Inline-Anlage (Aufgabe / Meeting) ---
  const resetTask = () => setTForm({ name: '', abschluss_start: ap.start_date, abschluss_end: ap.end_date });
  const resetMeeting = () => setMForm({ titel: '', date: ap.start_date, time: '', typ: 'Team Meeting' });
  const canCreateTask = tForm.name.trim().length > 0;
  const canCreateMeeting = mForm.date.trim().length > 0;

  const openAdd = (mode: Exclude<AddMode, null>) => {
    if (mode === 'task') resetTask(); else resetMeeting();
    setAddMode((m) => (m === mode ? null : mode));
  };

  const submitTask = async () => {
    if (!canCreateTask || busy) return;
    setBusy(true);
    try {
      await onCreateTask({ name: tForm.name.trim(), abschluss_start: tForm.abschluss_start || null, abschluss_end: tForm.abschluss_end || null });
      resetTask();
      setAddMode(null);
    } finally { setBusy(false); }
  };

  const submitMeeting = async () => {
    if (!canCreateMeeting || busy) return;
    setBusy(true);
    try {
      await onCreateMeeting({ titel: mForm.titel.trim(), date: mForm.date, time: mForm.time, typ: mForm.typ });
      resetMeeting();
      setAddMode(null);
    } finally { setBusy(false); }
  };

  return (
    <PmCard>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="subtitle1" sx={{ fontWeight: 600, fontFamily: 'monospace' }}>{apLabel(ap.nr)}</Typography>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{ap.titel ?? '—'}</Typography>
          <PmPill tone="neutral">
            {apStart.format('DD.MM.')} – {apEnd.format('DD.MM.YYYY')}
          </PmPill>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          <ToggleButtonGroup
            size="small" exclusive value={zoom}
            onChange={(_e, v) => { if (v) setZoom(v as 'week' | 'month'); }}
            aria-label={t('gantt.scale', 'Zeitraster')}
          >
            <ToggleButton value="week" sx={{ px: 1, py: 0.25, fontSize: '0.7rem' }}>{t('gantt.week', 'KW')}</ToggleButton>
            <ToggleButton value="month" sx={{ px: 1, py: 0.25, fontSize: '0.7rem' }}>{t('gantt.month', 'Monat')}</ToggleButton>
          </ToggleButtonGroup>
          <Button size="small" startIcon={<AddIcon />} onClick={() => openAdd('task')}>
            {t('tasks.add', 'Aufgabe')}
          </Button>
          <Button size="small" startIcon={<EventIcon />} onClick={() => openAdd('meeting')}>
            {t('meetings.addShort', 'Meeting')}
          </Button>
        </Stack>
      </Stack>

      {/* Zeitachsen-Kopf: Raster je Kalenderwoche bzw. Monat (umschaltbar) */}
      <Box sx={{ display: 'flex', alignItems: 'stretch', mb: 0.5 }}>
        <Box sx={{ width: NAME_COL, flexShrink: 0 }} />
        <Box sx={{ position: 'relative', flex: 1, height: 18, borderBottom: '1px solid var(--pm-bg-muted)' }}>
          {headerMarks.map((mk, i) => (
            <Box key={i} sx={{ position: 'absolute', left: `${mk.left}%`, top: 0, bottom: 0 }}>
              <Box sx={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: '1px', background: 'var(--pm-bg-muted)' }} />
              <Typography
                variant="caption"
                sx={{ position: 'absolute', left: 2, top: 0, color: 'var(--pm-fg-muted)', fontSize: '0.66rem', whiteSpace: 'nowrap' }}
              >
                {mk.label}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Meeting-Spur: Rauten am jeweiligen Datum */}
      {meetings.length > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 22 }}>
          <Box sx={{ width: NAME_COL, flexShrink: 0, pr: 1 }}>
            <Typography variant="caption" sx={{ color: 'var(--pm-fg-subtle)' }}>{t('meetings.title', 'Meetings')}</Typography>
          </Box>
          <Box sx={{ position: 'relative', flex: 1, height: 18 }}>
            {meetings.map((m) => {
              const { date, time } = splitIsoDateTime(m.datum);
              const left = pct(date);
              if (left == null) return null;
              const label = `${m.titel || m.typ} · ${dayjs(date).format('DD.MM.')}${time ? ' ' + time : ''} · ${m.typ}`;
              return (
                <Tooltip key={m.id} title={label}>
                  <Box
                    onClick={() => onEditMeeting(m)}
                    aria-label={`${tc('actions.edit', 'Bearbeiten')}: ${m.titel || m.typ}`}
                    sx={{
                      position: 'absolute', left: `${left}%`, top: 0, transform: 'translateX(-50%)',
                      cursor: 'pointer', fontSize: '1rem', lineHeight: 1, userSelect: 'none',
                    }}
                  >
                    {meetingTypeSymbol(m.typ)}
                  </Box>
                </Tooltip>
              );
            })}
          </Box>
        </Box>
      )}

      {tasks.length === 0 && addMode == null && (
        <Typography variant="caption" sx={{ color: 'var(--pm-fg-subtle)', display: 'block', py: 1.5, pl: `${NAME_COL}px` }}>
          {t('tasks.emptyForAp', 'Noch keine Aufgaben in diesem Arbeitspaket. „Aufgabe" legt die erste an.')}
        </Typography>
      )}

      <Stack spacing={0.5}>
        {tasks.map((task) => {
          const isDragging = drag?.taskId === task.id && preview != null;
          const effStart = isDragging ? preview!.start : task.abschluss_start;
          const effEnd = isDragging ? preview!.end : task.abschluss_end;
          const left = pct(effStart);
          const right = pct(effEnd);
          const hasBar = left != null && right != null;
          const barW = hasBar ? Math.max(1.5, (right as number) - (left as number)) : 0;
          const color = TASK_STATE_COLOR[task.state ?? 'PLANNED'] ?? 'var(--pm-gray-400)';
          return (
            <Box key={task.id} sx={{ display: 'flex', alignItems: 'center', minHeight: 28 }}>
              <Box sx={{ width: NAME_COL, flexShrink: 0, pr: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="body2" noWrap sx={{ flex: 1 }} title={task.name}>{task.name}</Typography>
                <IconButton
                  size="small" onClick={() => onEditTask(task)}
                  aria-label={`${tc('actions.edit', 'Bearbeiten')}: ${task.name}`}
                  sx={{ color: 'var(--pm-fg-subtle)', p: 0.25 }}
                >
                  <EditIcon sx={{ fontSize: 15 }} />
                </IconButton>
              </Box>
              <Box data-task-track sx={{ position: 'relative', flex: 1, height: 20, background: 'var(--pm-bg-deep)', borderRadius: 1 }}>
                {hasBar ? (
                  <Tooltip title={`${dayjs(effStart).format('DD.MM.')} – ${dayjs(effEnd).format('DD.MM.YYYY')}`}>
                    <Box
                      sx={{
                        position: 'absolute', top: 2, height: 16, borderRadius: 1,
                        left: `${left}%`, width: `${barW}%`, minWidth: 8,
                        background: color, opacity: isDragging ? 1 : 0.85,
                        boxShadow: isDragging ? '0 0 0 1px var(--pm-fg-base)' : 'none',
                      }}
                    >
                      <Box onMouseDown={(e) => startDrag(e, task, 'start')} sx={{ position: 'absolute', left: 0, top: 0, width: 8, height: '100%', cursor: 'ew-resize' }} />
                      <Box onMouseDown={(e) => startDrag(e, task, 'move')} sx={{ position: 'absolute', left: 8, right: 8, top: 0, height: '100%', cursor: drag ? 'grabbing' : 'grab' }} />
                      <Box onMouseDown={(e) => startDrag(e, task, 'end')} sx={{ position: 'absolute', right: 0, top: 0, width: 8, height: '100%', cursor: 'ew-resize' }} />
                    </Box>
                  </Tooltip>
                ) : (
                  <Typography variant="caption" sx={{ position: 'absolute', left: 6, top: 1, color: 'var(--pm-fg-subtle)', fontSize: '0.66rem' }}>
                    {t('tasks.noSchedule', 'kein Termin')}
                  </Typography>
                )}
              </Box>
            </Box>
          );
        })}
      </Stack>

      {/* Inline-Anlage Aufgabe */}
      {addMode === 'task' && (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1, pt: 1, borderTop: '1px dashed var(--pm-bg-muted)' }}>
          <TextField
            size="small" variant="standard" autoFocus
            placeholder={t('tasks.namePlaceholder', 'Aufgabenname…')}
            value={tForm.name}
            onChange={(e) => setTForm((f) => ({ ...f, name: e.target.value }))}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void submitTask(); } }}
            sx={{ flex: 1 }}
          />
          <TextField size="small" type="date" label={t('arbeitspakete.startDate', 'Start')} value={tForm.abschluss_start ?? ''} onChange={(e) => setTForm((f) => ({ ...f, abschluss_start: e.target.value || null }))} slotProps={{ inputLabel: { shrink: true } }} />
          <TextField size="small" type="date" label={t('arbeitspakete.endDate', 'Ende')} value={tForm.abschluss_end ?? ''} onChange={(e) => setTForm((f) => ({ ...f, abschluss_end: e.target.value || null }))} slotProps={{ inputLabel: { shrink: true } }} />
          <Tooltip title={tc('actions.save', 'Speichern')}>
            <span>
              <IconButton color="primary" size="small" onClick={() => void submitTask()} disabled={!canCreateTask || busy} aria-label={tc('actions.save', 'Speichern')}>
                <CheckIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <IconButton size="small" onClick={() => { setAddMode(null); resetTask(); }} aria-label={tc('actions.cancel', 'Abbrechen')}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      )}

      {/* Inline-Anlage Meeting */}
      {addMode === 'meeting' && (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1, pt: 1, borderTop: '1px dashed var(--pm-bg-muted)' }}>
          <TextField
            size="small" variant="standard" autoFocus
            placeholder={t('meetings.titelPlaceholder', 'Meeting-Titel…')}
            value={mForm.titel}
            onChange={(e) => setMForm((f) => ({ ...f, titel: e.target.value }))}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void submitMeeting(); } }}
            sx={{ flex: 1 }}
          />
          <TextField size="small" type="date" label={t('meetings.date', 'Datum')} value={mForm.date} onChange={(e) => setMForm((f) => ({ ...f, date: e.target.value }))} slotProps={{ inputLabel: { shrink: true } }} />
          <TextField size="small" type="time" label={t('meetings.time', 'Uhrzeit')} value={mForm.time} onChange={(e) => setMForm((f) => ({ ...f, time: e.target.value }))} slotProps={{ inputLabel: { shrink: true } }} sx={{ width: 110 }} />
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <Select value={mForm.typ} onChange={(e) => setMForm((f) => ({ ...f, typ: e.target.value }))}>
              {MEETING_TYPES.map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
            </Select>
          </FormControl>
          <Tooltip title={tc('actions.save', 'Speichern')}>
            <span>
              <IconButton color="primary" size="small" onClick={() => void submitMeeting()} disabled={!canCreateMeeting || busy} aria-label={tc('actions.save', 'Speichern')}>
                <CheckIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <IconButton size="small" onClick={() => { setAddMode(null); resetMeeting(); }} aria-label={tc('actions.cancel', 'Abbrechen')}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      )}
    </PmCard>
  );
}
